import React from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Inbox, FileText, BookOpen, Receipt, Megaphone, Sparkles,
  Calendar, MessageSquare, BarChart3, UsersRound, LogOut, ExternalLink, Bell,
  ClipboardList, Tag,
} from "lucide-react";
import Logo from "../../components/Logo";
import { useAuth } from "../../context/AuthContext";

const SECTIONS = [
  { to: "/admin", end: true, label: "Tableau de bord", icon: LayoutDashboard, testid: "admin-nav-dashboard" },
  { to: "/admin/clients", label: "Clients", icon: Users, testid: "admin-nav-clients" },
  { to: "/admin/questionnaires", label: "Questionnaires", icon: ClipboardList, testid: "admin-nav-questionnaires" },
  { to: "/admin/leads", label: "Leads", icon: Inbox, testid: "admin-nav-leads" },
  { to: "/admin/articles", label: "Articles", icon: FileText, testid: "admin-nav-articles" },
  { to: "/admin/magazines", label: "Magazines & Éditions", icon: BookOpen, testid: "admin-nav-magazines" },
  { to: "/admin/editions-speciales", label: "Éditions spéciales", icon: Sparkles, testid: "admin-nav-special" },
  { to: "/admin/devis", label: "Devis & Propositions", icon: Receipt, testid: "admin-nav-devis" },
  { to: "/admin/offres", label: "Catalogue d'offres", icon: Tag, testid: "admin-nav-offers" },
  { to: "/admin/annonceurs", label: "Annonceurs", icon: Megaphone, testid: "admin-nav-advertisers" },
  { to: "/admin/rdv", label: "Rendez-vous", icon: Calendar, testid: "admin-nav-rdv" },
  { to: "/admin/messagerie", label: "Messagerie", icon: MessageSquare, testid: "admin-nav-messaging" },
  { to: "/admin/sondages", label: "Sondages / Enquêtes", icon: BarChart3, testid: "admin-nav-polls" },
  { to: "/admin/equipe", label: "Équipe interne", icon: UsersRound, testid: "admin-nav-team" },
  { to: "/admin/console", label: "Console (legacy)", icon: ExternalLink, testid: "admin-nav-console" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex" style={{ background: "var(--gai-white)" }}>
      {/* Sidebar — black */}
      <aside
        className="w-[280px] shrink-0 flex flex-col"
        style={{
          background: "var(--gai-black)",
          borderRight: "1px solid var(--gai-graphite)",
        }}
        data-testid="admin-sidebar"
      >
        <div className="p-5" style={{ borderBottom: "1px solid var(--gai-graphite)" }}>
          <Logo size={44} withWord theme="dark" />
          <div
            className="mt-3 text-[0.62rem] font-bold tracking-[0.24em] uppercase"
            style={{ color: "var(--gai-red)" }}
          >
            Espace Administration
          </div>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            return (
              <NavLink
                key={s.to}
                to={s.to}
                end={s.end}
                data-testid={s.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-[0.8rem] transition-all ${
                    isActive ? "font-semibold" : ""
                  }`
                }
                style={({ isActive }) => ({
                  color: isActive ? "var(--gai-white)" : "rgba(255,255,255,0.65)",
                  background: isActive ? "rgba(227,30,36,0.12)" : "transparent",
                  borderLeft: isActive ? "3px solid var(--gai-red)" : "3px solid transparent",
                })}
              >
                <Icon size={16} />
                <span>{s.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div
          className="p-4 flex items-center gap-3"
          style={{ borderTop: "1px solid var(--gai-graphite)" }}
        >
          <div
            className="w-9 h-9 flex items-center justify-center font-bold text-sm"
            style={{ background: "var(--gai-red)", color: "var(--gai-white)" }}
          >
            {(user?.name || "A")[0].toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm truncate" style={{ color: "var(--gai-white)" }}>
              {user?.name || "Admin"}
            </div>
            <div
              className="text-[0.62rem] font-bold tracking-[0.22em] uppercase"
              style={{ color: "var(--gai-red)" }}
            >
              Administrateur
            </div>
          </div>
          <button
            onClick={async () => { await logout(); navigate("/"); }}
            className="p-2 hover:opacity-80"
            style={{ color: "rgba(255,255,255,0.55)" }}
            data-testid="admin-logout-btn"
            aria-label="Déconnexion"
          >
            <LogOut size={16} />
          </button>
        </div>
      </aside>

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0" style={{ background: "var(--gai-off-white)" }}>
        <header
          className="flex items-center justify-between px-8 h-[68px]"
          style={{
            background: "var(--gai-white)",
            borderBottom: "1px solid var(--gai-grey-200)",
          }}
        >
          <div />
          <div className="flex items-center gap-3">
            <Link to="/" className="gai-btn-ghost" data-testid="admin-see-site">
              <ExternalLink size={14} /> Voir le site
            </Link>
            <button
              className="p-2 relative"
              style={{ color: "var(--gai-black)" }}
              aria-label="Notifications"
              data-testid="admin-bell"
            >
              <Bell size={16} />
              <span
                className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full"
                style={{ background: "var(--gai-red)" }}
              />
            </button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
